import { useState } from 'react';
import { useMatch } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';

import { useSaladBar } from '@australiangreens/ag-internal-components';
import { useRedisWritesBlocked } from '@/apis/call';
import { SearchTreeNode } from '@/types/searchTreeTypes';
import { useDownloadPdf } from '@components/DistributionListPanel';
import type { OtherActionsPanelFormState } from '@components/OtherActionsPanelContent/shared/useOtherActionsPanelForm';
import { useApplyOtherActions } from '@components/OtherActionsPanelContentAdv/hooks';
import { useDeleteContactsFromList } from '@components/OtherActionsPanelContent/shared/useDeleteContactsFromList';
import { mapStatesForApplyOtherActions } from '@components/OtherActionsPanelContentAdv/util';

export function useOtherActionsPanelTreeApply({
  form,
  tree,
  listId,
  sortOrder,
  sortColumn,
  selectedContactIds,
  onClose,
}: {
  form: OtherActionsPanelFormState;
  tree?: SearchTreeNode;
  listId: number;
  sortOrder: string;
  sortColumn: string;
  selectedContactIds?: number[];
  onClose: (isSuccessful: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const match = useMatch('/lists/:listId/*');
  const { mutateAsync: applyOtherActions } = useApplyOtherActions();
  const { mutateAsync: deleteContactsFromList } = useDeleteContactsFromList(listId);
  const { enqueueSuccessNotification, enqueueErrorNotification } = useSaladBar();
  const writesBlocked = useRedisWritesBlocked();
  const [applyLoading, setApplyLoading] = useState(false);

  const downloadPdf = useDownloadPdf(
    form.distributionListElection.id,
    String(match?.params.listId ?? ''),
    sortOrder,
    sortColumn,
    form.distributionListNotes
  );

  const handleApply = async () => {
    if (writesBlocked) return;
    let isSuccessful = true;

    if (form.isAtLeastOneFieldSet && tree) {
      setApplyLoading(true);
      try {
        if (form.isDownloadPdfChecked) {
          await downloadPdf(selectedContactIds);
          enqueueSuccessNotification('Pdf Downloaded');
        }

        await applyOtherActions({
          tree,
          ...mapStatesForApplyOtherActions({
            groupAddEntities: form.groupAddEntities,
            groupRemoveEntities: form.groupRemoveEntities,
            newGroupNames: form.newGroupNames,
            tagAddEntities: form.tagAddEntities,
            tagRemoveEntities: form.tagRemoveEntities,
            updateRocketStatus: form.updateRocketStatus,
            electionValue: form.electionValue,
            taskKindType: form.taskKindType,
            levelOfInterest: form.levelOfInterest,
            updatePathwayStep: form.updatePathwayStep,
            updatePathwayOption: form.updatePathwayOption,
            notes: form.notes,
          }),
        });

        if (form.canDeleteFromList && selectedContactIds && selectedContactIds.length > 0) {
          await deleteContactsFromList(selectedContactIds);
        }
      } catch {
        isSuccessful = false;
        enqueueErrorNotification('Actions could not be applied.');
      } finally {
        setApplyLoading(false);
      }
    }

    if (!isSuccessful) return;

    queryClient.invalidateQueries({ queryKey: ['advsearch'] });
    if (listId) queryClient.invalidateQueries({ queryKey: ['list', listId] });
    form.resetInternalState();
    onClose(true);
  };

  return { applyLoading, handleApply };
}
